import React, { useState, useEffect } from 'react';
import { useDocumentProcessing } from './hooks/useDocumentProcessing';
import DocumentViewer from '../../components/viewers/DocumentViewer';
import MarkdownEditor from '../../components/editor/MarkdownEditor';
import { indexedDBService, StoredDocument } from './services/indexedDB';
import CVManager from './components/CVManager';
import SharedSidebar from '../../components/layout/SharedSidebar';
import { generateProfileFilename, extractNameFromMarkdown, getFallbackFilename } from '../../utils/fileNaming';
import './CVRedactor.css';

const CVRedactor: React.FC = () => {
  const documentProcessing = useDocumentProcessing();
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [currentStep, setCurrentStep] = useState<'original' | 'generated'>('original');
  const [hasExportedPDF, setHasExportedPDF] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [uploadedFileType, setUploadedFileType] = useState<'pdf' | 'docx' | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [generatedPdfFile, setGeneratedPdfFile] = useState<File | null>(null);
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState<string | null>(null);
  const [storedDocuments, setStoredDocuments] = useState<StoredDocument[]>([]);
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (uploadedFileUrl) {
        URL.revokeObjectURL(uploadedFileUrl);
      }
      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
      }
    };
  }, [uploadedFileUrl, generatedPdfUrl]);

  // Initialize IndexedDB and load stored documents
  useEffect(() => {
    const initDB = async () => {
      try {
        await indexedDBService.init();
        await loadDocuments();
      } catch (error) {
        console.error('Failed to initialize IndexedDB:', error);
      }
    };
    initDB();
  }, []);

  // Track if we're already loading to prevent double loads
  const [isLoadingLastDocument, setIsLoadingLastDocument] = useState(false);
  
  // Load last opened document after component mounts
  useEffect(() => {
    const loadLastDocument = async () => {
      const lastDocumentId = localStorage.getItem('lastOpenedDocument');
      if (lastDocumentId && storedDocuments.length > 0 && !isLoadingLastDocument) {
        setIsLoadingLastDocument(true);
        const doc = await indexedDBService.getDocument(lastDocumentId);
        if (doc) {
          await handleDocumentSelect(doc);
        }
      }
    };
    
    // Only load if we have documents and haven't loaded yet
    if (storedDocuments.length > 0 && !currentDocumentId && !isLoadingLastDocument) {
      loadLastDocument();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedDocuments]);

  const loadDocuments = async (page: number = 1) => {
    try {
      const result = await indexedDBService.getAllDocuments(page, 5);
      setStoredDocuments(result.documents);
      setTotalPages(result.pages);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  // Auto-close sidebar when file is uploaded
  useEffect(() => {
    if (documentProcessing.uploadedDocument && !hasUploadedFile) {
      setHasUploadedFile(true);
      setSidebarOpen(false);
      setCurrentDocumentId(documentProcessing.uploadedDocument.document_id);
      // Save to localStorage for persistence
      localStorage.setItem('lastOpenedDocument', documentProcessing.uploadedDocument.document_id);
      // Reset step to original when uploading new file
      setCurrentStep('original');
      // Clear markdown content to wait for processing result
      setMarkdownContent('');
    }
  }, [documentProcessing.uploadedDocument, hasUploadedFile]);

  // Keep sidebar open when no file uploaded
  useEffect(() => {
    if (!documentProcessing.uploadedDocument && hasUploadedFile) {
      setHasUploadedFile(false);
      setSidebarOpen(true);
    }
  }, [documentProcessing.uploadedDocument, hasUploadedFile]);

  // Update markdown content when processing completes
  useEffect(() => {
    if (documentProcessing.processingResult?.redacted_content) {
      setMarkdownContent(documentProcessing.processingResult.redacted_content);
    }
  }, [documentProcessing.processingResult]);

  // Handle PDF blob from DOCX conversion
  useEffect(() => {
    if (documentProcessing.pdfBlob && !uploadedFileUrl) {
      // Create URL from the converted PDF blob
      const url = URL.createObjectURL(documentProcessing.pdfBlob);
      setUploadedFileUrl(url);
      
      // Update file type to PDF since we're now showing the converted PDF
      setUploadedFileType('pdf');
    }
  }, [documentProcessing.pdfBlob, uploadedFileUrl]); // Depend on both to avoid recreating URLs

  const handleFileUpload = async (files: FileList) => {
    if (files.length > 0) {
      const file = files[0];
      
      // First reset the document processing state to clear any previous document
      documentProcessing.resetState();
      
      // Clean up previous URLs
      if (uploadedFileUrl) {
        URL.revokeObjectURL(uploadedFileUrl);
      }
      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
      }
      
      // Clear all state from previous document
      setMarkdownContent(''); // Clear markdown content
      setUploadedFile(null);
      setUploadedFileType(null);
      setUploadedFileUrl(null);
      setGeneratedPdfFile(null);
      setGeneratedPdfUrl(null);
      setHasExportedPDF(false);
      setCurrentStep('original');
      
      // Detect file type
      let fileType: 'pdf' | 'docx' = 'pdf';
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.pdf')) {
        fileType = 'pdf';
      } else if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
        fileType = 'docx';
      }
      
      // Set new file
      setUploadedFile(file);
      setUploadedFileType(fileType);
      
      // For PDFs, create URL immediately
      if (fileType === 'pdf') {
        const url = URL.createObjectURL(file);
        setUploadedFileUrl(url);
      }
      // For DOCX files, wait for conversion and use the PDF blob
      
      // Upload and start processing
      await documentProcessing.uploadFile(file);
      // Reload documents list after upload
      await loadDocuments(currentPage);
    }
  };

  const handleDocumentSelect = async (document: StoredDocument) => {
    try {
      
      // Save to localStorage for persistence across refreshes
      localStorage.setItem('lastOpenedDocument', document.documentId);
      
      // Load the document from IndexedDB
      setCurrentDocumentId(document.documentId);
      
      // Clear any existing state and URLs
      if (uploadedFileUrl) {
        URL.revokeObjectURL(uploadedFileUrl);
        setUploadedFileUrl(null);
      }
      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
        setGeneratedPdfUrl(null);
      }
      setUploadedFileType(null);
      setGeneratedPdfFile(null);
      setHasExportedPDF(false);
      setCurrentStep('original');
      
      // Check if we have a PDF blob (for converted DOCX files) or original file
      const pdfBlob = (document as any).pdfBlob;
      const fileToLoad = pdfBlob || document.originalFile;
      
      if (fileToLoad) {
        try {
          // Ensure we have a valid Blob
          const blob = fileToLoad instanceof Blob 
            ? fileToLoad 
            : new Blob([fileToLoad], { type: pdfBlob ? 'application/pdf' : document.fileType });
          
          // Determine the file name and type
          let fileName = document.fileName;
          let mimeType = document.fileType || 'application/pdf';
          let fileType: 'pdf' | 'docx' = 'pdf';
          
          if (pdfBlob) {
            // If we have a PDF blob, it means this was a converted DOCX
            fileName = fileName.replace(/\.(docx?|doc)$/i, '.pdf');
            mimeType = 'application/pdf';
            fileType = 'pdf';
          } else {
            // Detect file type from filename or mime type
            const fileNameLower = fileName.toLowerCase();
            if (fileNameLower.endsWith('.pdf') || mimeType?.includes('pdf')) {
              fileType = 'pdf';
            } else if (fileNameLower.endsWith('.docx') || fileNameLower.endsWith('.doc') || 
                       mimeType?.includes('word') || mimeType?.includes('document')) {
              fileType = 'docx';
            }
          }
          
          // Create a File object from the stored Blob
          const file = new File([blob], fileName, { 
            type: mimeType,
            lastModified: Date.now()
          });
          
          setUploadedFile(file);
          setUploadedFileType(fileType);
          
          // Create blob URL
          const url = URL.createObjectURL(file);
          setUploadedFileUrl(url);
          setHasUploadedFile(true);
        } catch (error) {
          console.error('Error creating file from blob:', error);
        }
      }
      
      // Load the markdown content (edited or original)
      if (document.editedMarkdown) {
        setMarkdownContent(document.editedMarkdown);
      } else if (document.originalMarkdown) {
        setMarkdownContent(document.originalMarkdown);
      }
      
      // Load the exported PDF if it exists
      if (document.exportedPDF) {
        try {
          // Ensure we have a valid Blob
          const blob = document.exportedPDF instanceof Blob 
            ? document.exportedPDF 
            : new Blob([document.exportedPDF], { type: 'application/pdf' });
          
          // Create a File object from the stored Blob
          const pdfFile = new File([blob], 'generated.pdf', { 
            type: 'application/pdf',
            lastModified: Date.now()
          });
          setGeneratedPdfFile(pdfFile);
          const url = URL.createObjectURL(pdfFile);
          setGeneratedPdfUrl(url);
          setHasExportedPDF(true);
        } catch (error) {
          console.error('Error creating PDF file from blob:', error);
        }
      }
      
      // Show the document in workspace
      // Need to reset document processing state to show the loaded document
      documentProcessing.resetState();
      
      // Close the sidebar
      setSidebarOpen(false);
    } catch (error) {
      console.error('Failed to load document:', error);
      alert('Failed to load document. Please try again.');
    }
  };

  const handleDocumentDelete = async (document: StoredDocument, event: React.MouseEvent) => {
    // Stop propagation to prevent document selection
    event.stopPropagation();
    
    if (window.confirm(`Are you sure you want to delete "${document.fileName}"? This action cannot be undone.`)) {
      try {
        await indexedDBService.deleteDocument(document.documentId);
        
        // If the deleted document was currently open, clear the workspace
        if (currentDocumentId === document.documentId) {
          // Reset document processing state (clears uploadedDocument, processingResult, etc.)
          documentProcessing.resetState();
          
          // Clear local state
          setUploadedFile(null);
          setUploadedFileType(null);
          setMarkdownContent('');
          setGeneratedPdfFile(null);
          // Clean up URLs
          if (uploadedFileUrl) {
            URL.revokeObjectURL(uploadedFileUrl);
            setUploadedFileUrl(null);
          }
          if (generatedPdfUrl) {
            URL.revokeObjectURL(generatedPdfUrl);
            setGeneratedPdfUrl(null);
          }
          setHasExportedPDF(false);
          setCurrentDocumentId(null);
          setHasUploadedFile(false);
          // Clear from localStorage too
          localStorage.removeItem('lastOpenedDocument');
        }
        
        // Reload the document list
        await loadDocuments(currentPage);
      } catch (error) {
        console.error('Failed to delete document:', error);
        alert('Failed to delete document. Please try again.');
      }
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const toggleStep = (step: 'original' | 'generated') => {
    setCurrentStep(step);
  };

  return (
    <div className="cv-redactor-container">
      {/* SharedSidebar with CVManager content */}
      <SharedSidebar
        isOpen={isSidebarOpen}
        onToggle={toggleSidebar}
        title="Document Manager"
        subtitle="Upload files and manage your CV redaction history"
      >
        <CVManager
          storedDocuments={storedDocuments}
          currentDocumentId={currentDocumentId}
          currentPage={currentPage}
          totalPages={totalPages}
          onFileUpload={handleFileUpload}
          onDocumentSelect={handleDocumentSelect}
          onDocumentDelete={handleDocumentDelete}
          onPageChange={loadDocuments}
        />
      </SharedSidebar>

      <div className="workspace">
        {(documentProcessing.uploadedDocument || uploadedFile) && (
          <>
            {/* Step Indicator - Only show after Export PDF button is clicked */}
            {hasExportedPDF && (
              <div className="step-indicator">
                <div 
                  className={`step-dot ${currentStep === 'original' ? 'active' : 'completed'}`} 
                  title="Original Document"
                  onClick={() => toggleStep('original')}
                />
                <div 
                  className={`step-dot ${currentStep === 'generated' ? 'active' : ''}`} 
                  title="Generated Document"
                  onClick={() => toggleStep('generated')}
                />
              </div>
            )}

            <div className="panels-container">
          {/* Original PDF Panel */}
          <div className={`panel ${currentStep === 'generated' && hasExportedPDF ? 'hide' : ''}`}>
            <div className="panel-header">
              <div className="panel-title">
                <div className="status-indicator"></div>
                <span>Original Document</span>
              </div>
              <div className="panel-controls">
              </div>
            </div>
            <div className="panel-content">
              {uploadedFileUrl && uploadedFileType ? (
                <DocumentViewer
                  key={uploadedFileUrl}
                  file={uploadedFileUrl}
                  fileType={uploadedFileType}
                  height={600}
                  showToolbar={true}
                  onError={(error) => console.error('Document viewer error:', error)}
                />
              ) : (
                <div className="pdf-viewer hexagon-pattern">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <span className="material-icons" style={{ fontSize: '64px', color: 'var(--stone-grey)', opacity: '0.5', marginBottom: '16px', display: 'block' }}>upload_file</span>
                    <p style={{ color: 'var(--stone-grey)' }}>No document uploaded yet</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Markdown Editor Panel (Always Visible) */}
          <div className="panel editor-panel">
            <div className="panel-header">
              <div className="panel-title">
                <span>Markdown Editor</span>
              </div>
              <div className="panel-controls">
                <button 
                  className="control-btn" 
                  title="Save"
                  onClick={async () => {
                    if (markdownContent && currentDocumentId) {
                      try {
                        await indexedDBService.updateEditedMarkdown(currentDocumentId, markdownContent);
                        // Reload documents to update the list
                        await loadDocuments(currentPage);
                      } catch (error) {
                        console.error('Failed to save content:', error);
                        alert('Failed to save content. Please try again.');
                      }
                    }
                  }}
                >
                  <span className="material-icons">save</span>
                </button>
                <button 
                  className="control-btn" 
                  title="Reset to original"
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to reset to the original redacted content?')) {
                      if (currentDocumentId) {
                        try {
                          const doc = await indexedDBService.getDocument(currentDocumentId);
                          if (doc?.originalMarkdown) {
                            setMarkdownContent(doc.originalMarkdown);
                          } else {
                            setMarkdownContent(documentProcessing.processingResult?.redacted_content || '');
                          }
                        } catch (error) {
                          console.error('Failed to load original content:', error);
                          setMarkdownContent(documentProcessing.processingResult?.redacted_content || '');
                        }
                      } else {
                        setMarkdownContent(documentProcessing.processingResult?.redacted_content || '');
                      }
                    }
                  }}
                >
                  <span className="material-icons">refresh</span>
                </button>
                <button 
                  className="export-btn" 
                  onClick={async () => {
                    setIsGeneratingPDF(true);
                    try {
                      // Get the current markdown content
                      const content = markdownContent || documentProcessing.processingResult?.redacted_content || '';
                      
                      // Call the backend API to generate PDF
                      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/pdf/markdown-to-pdf`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          markdown_content: content,
                          style: 'profiler',
                          theme: 'athlete',
                          filename: `cv_${new Date().toISOString().slice(0, 10)}.pdf`,
                          feature_type: 'provider'  // Enables Confidential footer for Providers
                        }),
                      });
                      
                      if (!response.ok) {
                        throw new Error('PDF generation failed');
                      }
                      
                      // Get the PDF blob
                      const blob = await response.blob();
                      
                      // Create a File object from the blob
                      const pdfFile = new File([blob], `cv_${new Date().toISOString().slice(0, 10)}.pdf`, {
                        type: 'application/pdf',
                        lastModified: Date.now()
                      });
                      setGeneratedPdfFile(pdfFile);
                      const pdfUrl = URL.createObjectURL(pdfFile);
                      setGeneratedPdfUrl(pdfUrl);
                      
                      // Save edited markdown and PDF to IndexedDB
                      if (currentDocumentId) {
                        try {
                          await indexedDBService.updateEditedMarkdown(currentDocumentId, content);
                          await indexedDBService.updateExportedPDF(currentDocumentId, blob);
                        } catch (dbError) {
                          console.error('Failed to save to IndexedDB:', dbError);
                        }
                      }
                      
                      setHasExportedPDF(true);
                      setCurrentStep('generated');
                      
                      // Reload documents to update the list
                      await loadDocuments(currentPage);
                      
                    } catch (error) {
                      console.error('Error generating PDF:', error);
                      alert('Failed to generate PDF. Please try again.');
                    } finally {
                      setIsGeneratingPDF(false);
                    }
                  }}
                  disabled={isGeneratingPDF || (!markdownContent && !documentProcessing.processingResult)}
                >
                  <span className="material-icons">picture_as_pdf</span>
                  {isGeneratingPDF ? 'Generating...' : 'Export PDF'}
                </button>
              </div>
            </div>
            <div className="panel-content" style={{ position: 'relative' }}>
              {isGeneratingPDF && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  zIndex: 10,
                  background: 'rgba(255, 255, 255, 0.95)',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div className="spinner" style={{ marginBottom: '16px', margin: '0 auto' }}></div>
                  <p style={{ color: 'var(--stone-grey)' }}>Generating PDF...</p>
                  <small style={{ color: 'var(--stone-grey)', fontSize: '12px' }}>Using MD2PDF with consulting style</small>
                </div>
              )}
              {documentProcessing.isUploading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  zIndex: 10,
                  background: 'rgba(255, 255, 255, 0.95)',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div className="spinner" style={{ marginBottom: '16px' }}></div>
                  <p style={{ color: 'var(--stone-grey)' }}>Uploading document...</p>
                  <small style={{ color: 'var(--stone-grey)', fontSize: '12px' }}>Please wait</small>
                </div>
              )}
              {documentProcessing.isProcessing && !documentProcessing.processingResult && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  zIndex: 10,
                  background: 'rgba(255, 255, 255, 0.95)',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div className="spinner" style={{ marginBottom: '16px' }}></div>
                  <p style={{ color: 'var(--stone-grey)' }}>Processing document with AI...</p>
                  <small style={{ color: 'var(--stone-grey)', fontSize: '12px' }}>This may take a moment</small>
                </div>
              )}
              <MarkdownEditor
                value={markdownContent || documentProcessing.processingResult?.redacted_content || '# Your CV\n\nStart by uploading a document to see the redacted content here.'}
                onChange={setMarkdownContent}
                height={500}
                placeholder="Start editing your CV..."
                readOnly={!documentProcessing.processingResult && !markdownContent}
                onAutoSave={async (_content) => {
                  // Auto-save functionality can be implemented here
                }}
              />
            </div>
          </div>

          {/* Generated PDF Panel (Initially Hidden) */}
          <div className={`panel ${currentStep !== 'generated' || !hasExportedPDF ? 'hide' : ''}`}>
            <div className="panel-header">
              <div className="panel-title">
                <span>Generated PDF</span>
              </div>
              <div className="panel-controls">
                <button
                  className="download-btn"
                  onClick={() => {
                    if (generatedPdfFile) {
                      const url = URL.createObjectURL(generatedPdfFile);
                      const link = document.createElement('a');
                      link.href = url;
                      // Providers use the IC_CV naming convention plus the SSI_ download prefix.
                      const { firstName, lastName } = extractNameFromMarkdown(markdownContent);
                      const baseFilename = firstName || lastName
                        ? generateProfileFilename({ firstName, lastName, format: 'pdf', variant: 'ic' })
                        : getFallbackFilename('profile', 'pdf', uploadedFile?.name, 'ic');
                      link.download = `SSI_${baseFilename}`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                    }
                  }}
                >
                  <span className="material-icons">download</span>
                  Download
                </button>
                {/* SharePoint button hidden for now
                <button className="sharepoint-btn">
                  <span className="material-icons">cloud_upload</span>
                  SharePoint
                </button>
                */}
              </div>
            </div>
            <div className="panel-content">
              {generatedPdfUrl ? (
                <DocumentViewer
                  key={generatedPdfUrl}
                  file={generatedPdfUrl}
                  fileType="pdf"
                  height={600}
                  showToolbar={true}
                  onError={(error) => console.error('PDF viewer error:', error)}
                />
              ) : (
                <div className="pdf-viewer hexagon-pattern">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: 'var(--stone-grey)' }}>No generated content yet</p>
                  </div>
                </div>
              )}
            </div>
          </div>
            </div>
          </>
        )}
        
        {!documentProcessing.uploadedDocument && !uploadedFile && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            flexDirection: 'column',
            textAlign: 'center',
            padding: '40px'
          }}>
            <span className="material-icons" style={{ 
              fontSize: '96px', 
              color: 'rgba(255,255,255,0.3)', 
              marginBottom: '24px' 
            }}>description</span>
            <h2 style={{ 
              color: 'rgba(255,255,255,0.8)', 
              fontSize: '24px', 
              fontWeight: '500', 
              marginBottom: '16px' 
            }}>Welcome to CV Redactor</h2>
            <p style={{ 
              color: 'rgba(255,255,255,0.6)', 
              fontSize: '16px',
              maxWidth: '400px',
              lineHeight: '1.6'
            }}>Select documents from the sidebar or upload files to begin the redaction process</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CVRedactor;
